import { Accessor, createSignal, Setter } from "solid-js";
import { AttendanceCodeInfo, DocumentCategoryOption, StaffSectionData, StatusVerification, StudentSectionData } from "../../api";
import { CalendarDisplayMode } from "../st-schedule-grid/st-schedule-grid";
import { date, toast } from "common";
import attendanceGridApi from '../../api/st-attendance-grid';
import { attendanceReportService } from './services/AttendanceReportService';
import { CalendarService } from './services/CalendarService';
import { SectionVerificationService } from './services/SectionVerificationService';
import { AttendanceVerificationService } from './services/AttendanceVerificationService';
import { useJobQueueEvents } from './hooks/useJobQueueEvents';

const { startOfWeek, addDays, addYears } = date;

/**
 * Refactored AttendanceGridContext - focused on state management and orchestration
 * Complex logic delegated to service classes
 */
export class AttendanceGridContext {
    // ========== Core State ==========
    attendance = createSignal<StaffSectionData[]>([]);
    attendanceCodesMap = createSignal<Map<string, AttendanceCodeInfo>>(new Map());
    isLoading = createSignal(true);

    // ========== Calendar State ==========
    displayMode: Accessor<CalendarDisplayMode>;
    setDisplayMode: Setter<CalendarDisplayMode>;
    day: Accessor<Date>;
    setDay: Setter<Date>;
    hoveredDay: Accessor<Date | undefined>;
    setHoveredDay: Setter<Date | undefined>;
    isEnabled = createSignal(true);

    // ========== Focus Date State ==========
    focusDateLoaded = createSignal(false);
    synergyToday = createSignal<Date>(date.today());

    // ========== Report State ==========
    isProcessingReport = createSignal(false);
    reportStatusMessage = createSignal<string>('');
    staffSchoolYearGU = createSignal<string>('');
    attendanceType = createSignal<number | undefined>(undefined);

    // ========== Document Storage State ==========
    pendingStaffAttendanceVerifyGU = createSignal<string>('');
    pendingJobQueueGU = createSignal<string>('');
    pendingStaffGU = createSignal<string>('');
    pendingStartDate = createSignal<string>('');
    pendingEndDate = createSignal<string>('');
    pendingDocCategory = createSignal<string>('');

    // ========== Document Category State ==========
    documentCategoryOptions = createSignal<DocumentCategoryOption[]>([]);
    selectedDocCategory = createSignal<string>('');

    // ========== Verification Status State ==========
    hasVerification = createSignal<boolean>(false);
    hasDocument = createSignal<boolean>(false);
    verificationDocumentGU = createSignal<string>('');
    verificationStatus = createSignal<string>('');
    isPending = createSignal<boolean>(false);
    isValidSchoolWeek = createSignal<boolean>(true);

    // ========== Pending Restoration State ==========
    pendingSectionRestorations = createSignal<{ sectionGU: string | null | undefined; status: number }[]>([]);

    // ========== Modal State ==========
    showSignatureModal = createSignal(false);

    constructor() {
        [this.displayMode, this.setDisplayMode] = createSignal<CalendarDisplayMode>('WorkWeek');
        [this.day, this.setDay] = createSignal(date.today());
        [this.hoveredDay, this.setHoveredDay] = createSignal<Date>();

        this.initializeFocusDate();
        this.setupJobQueueListeners();
        this.loadDocumentCategories();
    }

    private async loadDocumentCategories() {
        try {
            const result = await attendanceGridApi.GetDocumentCategories();
            if (result.success) {
                this.documentCategoryOptions[1](result.categories ?? []);
                if (result.savedDocCategory) {
                    this.selectedDocCategory[1](result.savedDocCategory);
                }
            } else {
                toast.notify('error',  'Failed to load document categories: ' + result.errorMessage);
            }
        } catch (error) {
            toast.notify('error', 'Failed to load document categories: ' + error);
        }
    }

    // ========== Initialization ==========

    private async initializeFocusDate() {
        try {
            const focusDateStr = await attendanceGridApi.GetFocusDate();
            if (focusDateStr) {
                const focusDate = new Date(focusDateStr);
                this.setDay(focusDate);
                this.synergyToday[1](focusDate);
            }
        } catch (error) {
            console.error('Failed to load focus date:', error);
        } finally {
            this.focusDateLoaded[1](true);
        }
    }

    private setupJobQueueListeners() {
        useJobQueueEvents({
            onProcessing: (_message, _jobQueueGU) => {
                this.reportStatusMessage[1]('Processing...');
            },
            onSuccess: async (_status, jobQueueGU) => {
                const pendingGU = this.pendingJobQueueGU[0]();
                if (!pendingGU || jobQueueGU !== pendingGU) return;
                this.pendingJobQueueGU[1]('');
                await this.handleReportSuccess(jobQueueGU);
            },
            onError: (message, jobQueueGU) => {
                const pendingGU = this.pendingJobQueueGU[0]();
                if (!pendingGU || jobQueueGU !== pendingGU) return;
                this.pendingJobQueueGU[1]('');
                this.isProcessingReport[1](false);
                this.reportStatusMessage[1](`Error: ${message || 'Report failed'}`);
            }
        });
    }

    private async handleReportSuccess(jobQueueGU: string) {
        // If we have pending attendance verification data, save the report as a document
        const staffAttVerifyGU = this.pendingStaffAttendanceVerifyGU[0]();
        const staffGU = this.pendingStaffGU[0]();
        const startDate = this.pendingStartDate[0]();
        const endDate = this.pendingEndDate[0]();
        const docCategory = this.pendingDocCategory[0]();

        if (staffAttVerifyGU && staffGU && jobQueueGU) {
            try {
                console.log('Saving attendance verification document...');
                const saveDocResponse = await attendanceGridApi.SaveAttendanceVerificationDocument({
                    jobQueueGU: jobQueueGU,
                    staffAttendanceVerifyGU: staffAttVerifyGU,
                    staffGU: staffGU,
                    startDate: startDate,
                    endDate: endDate,
                    docCategory: docCategory
                });

                if (saveDocResponse.success) {
                    console.log('Document saved with PersonAtcDocGU:', saveDocResponse.personAtcDocGU);
                } else {
                    console.error('Failed to save document:', saveDocResponse.errorMessage);
                }
            } catch (error) {
                console.error('Error saving attendance verification document:', error);
            }

            // Clear the pending state
            this.clearPendingState();

            // Reset section confirmation states back to None
            this.attendance[1](prev => prev.map(s => ({ ...s, status: StatusVerification.None })));

            // Refresh verification status so the UI updates (e.g. submit → download)
            await this.checkWeekVerification(new Date(startDate), new Date(endDate));
        }

        this.isProcessingReport[1](false);
        this.reportStatusMessage[1]('Report complete!');
    }

    private clearPendingState() {
        this.pendingStaffAttendanceVerifyGU[1]('');
        this.pendingJobQueueGU[1]('');
        this.pendingStaffGU[1]('');
        this.pendingStartDate[1]('');
        this.pendingEndDate[1]('');
        this.pendingDocCategory[1]('');
    }

    // ========== Attendance Methods ==========

    getAttendance(dt: Date, sectionGU: string): StudentSectionData[] {
        const section = this.attendance[0]().find(sect => sect.sectionGU === sectionGU);
        if (!section) return [];

        const dateStr = CalendarService.formatDateForApi(dt);
        return section.studentSectionData.filter(stuData => this.sameDay(stuData.date!, dateStr));
    }

    sameDay(strDateA: string, strDateB: string): boolean
    {
        const dateA = new Date(strDateA);
        const dateB = new Date(strDateB);

        return dateA.getFullYear() == dateB.getFullYear()
            && dateA.getMonth() == dateB.getMonth()
            && dateA.getDate() == dateB.getDate();
    }

    getAttendanceCodeInfo(absenceCode: string): AttendanceCodeInfo | undefined {
        return this.attendanceCodesMap[0]().get(absenceCode);
    }

    // ========== Section Verification Methods ==========

    async handleConfirmSection(sectionGU: string) {
        // Update local state immediately (optimistic)
        this.attendance[1](prev =>
            SectionVerificationService.confirmSection(prev, sectionGU)
        );

        // If an Approved record already exists, flag re-verification immediately for instant UI feedback
        if (this.hasVerification[0]()) {
            this.isPending[1](true);
        }

        // Persist to DB as a Pending draft
        try {
            const startDate = addDays(startOfWeek(this.day()), 1);
            const endDate = addDays(startOfWeek(this.day()), 5);
            await attendanceGridApi.SaveSectionConfirmation({
                staffSchoolYearGU: this.staffSchoolYearGU[0](),
                startDate: CalendarService.formatDateForApi(startDate),
                endDate: CalendarService.formatDateForApi(endDate),
                sectionGU,
                status: 1 // StatusVerification.Accepted
            });
        } catch (error) {
            console.error('Failed to save section confirmation:', error);
        }
    }

    allSectionsReviewed(): boolean {
        return SectionVerificationService.allSectionsReviewed(this.attendance[0]());
    }

    // ========== Verification Methods ==========

    async checkWeekVerification(startDate: Date, endDate: Date) {
        try {
            const result = await AttendanceVerificationService.checkWeekVerification(startDate, endDate);
            this.hasVerification[1](result.hasVerification);
            this.hasDocument[1](result.hasDocument);
            this.verificationDocumentGU[1](result.documentGU);
            this.verificationStatus[1](result.status);
            this.isPending[1](result.isPending);

            // If there's a Pending draft, store the saved section states to restore after sections load
            if (result.isPending && result.sectionVerifications?.length) {
                this.pendingSectionRestorations[1](result.sectionVerifications as { sectionGU: string | null | undefined; status: number }[]);
            } else {
                this.pendingSectionRestorations[1]([]);
            }
        } catch (error) {
            console.error('Failed to check verification status:', error);
            // Reset to allow normal flow if check fails
            this.hasVerification[1](false);
            this.hasDocument[1](false);
            this.verificationDocumentGU[1]('');
            this.verificationStatus[1]('');
            this.isPending[1](false);
            this.pendingSectionRestorations[1]([]);
        }
    }

    async downloadDocument() {
        const docGU = this.verificationDocumentGU[0]();
        try {
            await AttendanceVerificationService.downloadDocument(docGU);
        } catch (error) {
            console.error('Error downloading document:', error);
        }
    }

    async cancelReVerification() {
        try {
            const startDate = addDays(startOfWeek(this.day()), 1);
            const endDate = addDays(startOfWeek(this.day()), 5);
            await attendanceGridApi.CancelPendingVerification({
                startDate: CalendarService.formatDateForApi(startDate),
                endDate: CalendarService.formatDateForApi(endDate)
            });
            this.isPending[1](false);
            this.pendingSectionRestorations[1]([]);
            // Reset all in-progress confirmations back to None
            this.attendance[1](prev =>
                prev.map(s => ({ ...s, status: StatusVerification.None }))
            );
        } catch (error) {
            console.error('Failed to cancel re-verification:', error);
        }
    }

    // ========== Report Methods ==========

    async handleSubmit() {
        // Prevent duplicate submissions while processing
        if (this.isProcessingReport[0]()) {
            return;
        }

        if (this.allSectionsReviewed()) {
            // Check if the user already has a signature with image data on file
            try {
                const checkResult = await attendanceGridApi.CheckExistingSignature();
                if (checkResult.success && checkResult.hasSignature && checkResult.signatureGU) {
                    // Signature on file - skip modal and submit directly
                    await this.submitReportWithSignature('', checkResult.signatureGU);
                } else {
                    // No signature on file - show modal to capture one
                    this.showSignatureModal[1](true);
                }
            } catch (error) {
                console.error('Failed to check existing signature, falling back to modal:', error);
                this.showSignatureModal[1](true);
            }
        }
    }

    async submitReportWithSignature(signatureBase64: string, existingSignatureGU?: string) {
        this.showSignatureModal[1](false);
        this.isProcessingReport[1](true);
        this.reportStatusMessage[1]('Saving attendance verification...');

        try {
            // Step 1: Calculate start and end dates for the current week
            const startDate = addDays(startOfWeek(this.day()), 1); // Monday
            const endDate = addDays(startOfWeek(this.day()), 5);   // Friday
            const startDateStr = CalendarService.formatDateForApi(startDate);
            const endDateStr = CalendarService.formatDateForApi(endDate);

            // Step 2: Prepare section verification data
            const sectionVerifications = SectionVerificationService.prepareSectionVerifications(
                this.attendance[0]()
            );

            // Step 3: Save signature and create database records
            const saveSignatureResponse = await attendanceGridApi.SaveSignature({
                signatureBase64: signatureBase64,
                personGU: window.security?.OriginalLoginSecurity?.UserGU,
                staffSchoolYearGU: this.staffSchoolYearGU[0](),
                startDate: startDateStr,
                endDate: endDateStr,
                sectionVerifications: sectionVerifications,
                existingSignatureGU: existingSignatureGU
            });

            if (!saveSignatureResponse.success) {
                throw new Error(saveSignatureResponse.errorMessage || 'Failed to save signature');
            }

            console.log('Signature saved with StaffAttendanceVerify GUID:', saveSignatureResponse.staffAttendanceVerifyGU);
            console.log('RevPersonSignature GUID:', saveSignatureResponse.signatureGU);

            // Store these for later use when the report completes
            this.pendingStaffAttendanceVerifyGU[1](saveSignatureResponse.staffAttendanceVerifyGU ?? '');
            this.pendingStaffGU[1](window.security?.OriginalLoginSecurity?.UserGU ?? '');
            this.pendingStartDate[1](startDateStr);
            this.pendingEndDate[1](endDateStr);
            this.pendingDocCategory[1](this.selectedDocCategory[0]());

            this.reportStatusMessage[1]('Queuing report job...');

            // Step 4: Queue the report job and get the Job Queue GUID
            const jobQueueGU = await attendanceReportService.generateWeeklyReport(
                this.day(),
                this.staffSchoolYearGU[0](),
                saveSignatureResponse.signatureGU ?? '',
                this.attendanceType[0]()
            );

            console.log('Report job queued with GUID:', jobQueueGU);
            this.pendingJobQueueGU[1](jobQueueGU);
            this.reportStatusMessage[1]('Processing report...');

            // Let GB's job queue system handle the polling and status updates
            if (window.gbapp) {
                window.gbapp.SetJQGU(jobQueueGU, true);
            } else {
                throw new Error('GB app not available - job queue monitoring not available');
            }

            // The ReportSuccess/ReportError event listeners will handle completion
        } catch (error: any) {
            console.error('Failed to generate report:', error);
            this.isProcessingReport[1](false);
            this.reportStatusMessage[1](`Error: ${error.message || 'Failed to generate report'}`);
        }
    }

    // ========== Calendar Helper Methods ==========

    weeks(): Date[] {
        return CalendarService.weeksFor(this.day());
    }

    dayEnabled(dt: Date): boolean {
        return this.isEnabled[0]();
    }

    firstDate(): Date | undefined {
        const focusDate = this.synergyToday[0]();
        return CalendarService.getDateRange(focusDate).firstDate;
    }

    lastDate(): Date | undefined {
        const focusDate = this.synergyToday[0]();
        return CalendarService.getDateRange(focusDate).lastDate;
    }

    years(): number[] {
        return CalendarService.years(this.firstDate(), this.lastDate());
    }

    monthEnabled(dt: Date): boolean {
        return CalendarService.monthEnabled(
            dt,
            this.firstDate(),
            this.lastDate(),
            this.isEnabled[0]()
        );
    }
}
